import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { GitMerge, Plus, Trash2, Link2, Sparkles, Check } from "lucide-react";
import logger from "@/lib/logger";

const ENTITY_TYPES = [
  "device", "requirement", "risk", "control", "verification",
  "clinical_evidence", "submission_section", "capa", "deviation",
  "change", "document", "training", "threat", "sbom",
  "iec62304_evidence", "iec62366_evidence", "pccp", "model_version",
  "ai_control", "patch_plan", "cer", "adverse_event", "udi_record",
  "fria", "ivdr_evidence", "test_case",
];
const RELATIONS = [
  "mitigates", "verifies", "implements", "evidences", "references",
  "derives_from", "closes", "impacts",
];

export default function Traceability() {
  const { loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");
  const [traces, setTraces] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [suggesting, setSuggesting] = useState(false);

  const [form, setForm] = useState({
    source_type: "risk", source_id: "",
    target_type: "control", target_id: "",
    relation: "mitigates", notes: "",
  });

  useEffect(() => {
    if (authLoading) return;
    api.get("/devices").then(({ data }) => {
      setProjects(data || []);
      if (data && data[0]) setPid(data[0].project_id);
    }).catch((e) => logger.warn("Load projects:", e?.response?.status));
  }, [authLoading]);

  const load = useCallback(() => {
    if (!pid) return;
    setLoading(true);
    Promise.all([
      api.get(`/traces?project_id=${encodeURIComponent(pid)}`),
      api.get(`/traces/coverage/${pid}`),
    ]).then(([tr, cov]) => {
      setTraces(tr.data || []);
      setCoverage(cov.data);
    }).catch((e) => logger.warn("Traces:", e?.response?.status))
      .finally(() => setLoading(false));
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!pid) { toast.error("Select a project first"); return; }
    if (!form.source_id || !form.target_id) { toast.error("Enter source and target IDs"); return; }
    setCreating(true);
    try {
      await api.post("/traces", { project_id: pid, ...form });
      toast.success("Trace link created");
      setForm({ ...form, source_id: "", target_id: "", notes: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  const removeTrace = async (trace_id) => {
    if (!window.confirm("Delete this trace link?")) return;
    try {
      await api.delete(`/traces/${trace_id}`);
      toast.success("Trace removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const requestSuggestions = async () => {
    if (!pid) { toast.error("Select a project first"); return; }
    setSuggesting(true);
    setSuggestions(null);
    try {
      const { data } = await api.post(`/traces/suggest/${pid}`);
      setSuggestions(data);
      toast.success(`${(data.suggestions || []).length} suggestion(s) returned`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suggestion call failed");
    } finally { setSuggesting(false); }
  };

  const acceptSuggestion = async (s) => {
    try {
      await api.post("/traces", {
        project_id: pid,
        source_type: s.source_type, source_id: s.source_id,
        target_type: s.target_type, target_id: s.target_id,
        relation: s.relation,
        notes: `[AI ${Math.round(s.confidence * 100)}%] ${s.rationale}`.slice(0, 1000),
      });
      // Remove accepted from local suggestions
      setSuggestions((prev) => prev && {
        ...prev,
        suggestions: prev.suggestions.filter((x) => !(x.source_id === s.source_id && x.target_id === s.target_id)),
      });
      toast.success("Link accepted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Accept failed");
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="mb-6">
          <div className="vq-overline mb-2 flex items-center gap-2"><GitMerge className="w-3 h-3" /> Traceability</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">
            Traceability Spine
          </h1>
          <p className="text-slate-600 mt-2 max-w-3xl">
            Link requirements → risks → controls → verification → clinical evidence → submission sections,
            so a reviewer can walk the trace both directions. Assistive links only — presence of a link does
            not by itself prove compliance or clinical adequacy.
          </p>
        </div>

        <div className="mb-6 max-w-md">
          <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Device project</Label>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="trace-project-select"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id} data-testid={`trace-project-${p.project_id}`}>{p.device_name || p.project_id}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {coverage && (
          <div className="vq-card p-5 mb-6" data-testid="trace-coverage-panel">
            <div className="vq-overline text-[10px] text-slate-500 mb-2">Coverage snapshot</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><div className="text-xs text-slate-500">Risks w/ control</div><div className="font-display font-bold text-xl" data-testid="trace-cov-risks">{coverage.risks_with_control_pct}%</div><div className="text-[11px] text-slate-500">{coverage.risks_total} risks</div></div>
              <div><div className="text-xs text-slate-500">Controls w/ verification</div><div className="font-display font-bold text-xl" data-testid="trace-cov-controls">{coverage.controls_with_verification_pct}%</div><div className="text-[11px] text-slate-500">{coverage.controls_total} controls</div></div>
              <div><div className="text-xs text-slate-500">Requirements w/ evidence</div><div className="font-display font-bold text-xl" data-testid="trace-cov-reqs">{coverage.requirements_with_evidence_pct}%</div><div className="text-[11px] text-slate-500">{coverage.requirements_total} requirements</div></div>
              <div><div className="text-xs text-slate-500">Sections w/ evidence</div><div className="font-display font-bold text-xl" data-testid="trace-cov-sections">{coverage.submission_sections_with_evidence_pct}%</div><div className="text-[11px] text-slate-500">{coverage.submission_sections_total} sections</div></div>
            </div>
            <div className="text-xs text-slate-500 mt-3">Total links recorded: <span data-testid="trace-total-links" className="font-mono font-semibold">{coverage.total_links}</span></div>
          </div>
        )}

        {/* Create trace form */}
        <div className="vq-card p-5 mb-6" data-testid="trace-create-panel">
          <div className="flex items-center gap-2 mb-3">
            <Plus className="w-4 h-4 text-slate-500" />
            <div className="font-display font-bold text-slate-900">Add trace link</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Source type</Label>
              <Select value={form.source_type} onValueChange={(v) => setForm({ ...form, source_type: v })}>
                <SelectTrigger data-testid="trace-source-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ENTITY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Source ID</Label>
              <Input value={form.source_id} onChange={(e) => setForm({ ...form, source_id: e.target.value })}
                     placeholder="e.g. risk_xxx" data-testid="trace-source-id" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Relation</Label>
              <Select value={form.relation} onValueChange={(v) => setForm({ ...form, relation: v })}>
                <SelectTrigger data-testid="trace-relation"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RELATIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-1" />
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Target type</Label>
              <Select value={form.target_type} onValueChange={(v) => setForm({ ...form, target_type: v })}>
                <SelectTrigger data-testid="trace-target-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ENTITY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Target ID</Label>
              <Input value={form.target_id} onChange={(e) => setForm({ ...form, target_id: e.target.value })}
                     placeholder="e.g. control_xxx or submission section key"
                     data-testid="trace-target-id" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Notes (optional)</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        placeholder="Short rationale for the link"
                        maxLength={1000}
                        data-testid="trace-notes" />
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={submit} disabled={creating || !pid} data-testid="trace-create-btn">
              <Link2 className="w-4 h-4 mr-1" /> {creating ? "Linking…" : "Create link"}
            </Button>
          </div>
        </div>

        {/* AI-assisted suggestions */}
        <div className="vq-card p-5 mb-6" data-testid="trace-suggest-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#0055FF]" />
                <div className="font-display font-bold text-slate-900">AI-assisted trace suggestions</div>
              </div>
              <div className="text-xs text-slate-500 mt-1 max-w-2xl">
                Claude reads this project&apos;s ISO 14971 risk register and proposes candidate
                risk-control → risk links. Assistive only — you review and accept each one.
              </div>
            </div>
            <Button onClick={requestSuggestions}
                    disabled={suggesting || !pid}
                    data-testid="trace-suggest-btn"
                    className="bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold">
              <Sparkles className="w-4 h-4 mr-1" /> {suggesting ? "Thinking…" : "Suggest links"}
            </Button>
          </div>

          {suggestions && (
            <div className="mt-4" data-testid="trace-suggest-result">
              {(suggestions.suggestions || []).length === 0 ? (
                <div className="text-xs text-slate-500 p-3 bg-slate-50 border border-slate-200 rounded"
                     data-testid="trace-suggest-empty">
                  {suggestions.note || "No suggestions returned."}
                </div>
              ) : (
                <div className="border border-slate-200 rounded overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 text-xs text-slate-600 border-b border-slate-200">
                    {suggestions.suggestions.length} candidate link(s) •{" "}
                    {suggestions.risks_considered} risks × {suggestions.controls_considered} controls considered
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Confidence</TableHead>
                        <TableHead>Source (control)</TableHead>
                        <TableHead>Target (risk)</TableHead>
                        <TableHead>Rationale</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {suggestions.suggestions.map((s) => (
                        <TableRow key={`${s.source_id}-${s.target_id}`}
                                  data-testid={`trace-sug-row-${s.source_id}-${s.target_id}`}>
                          <TableCell>
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-mono font-semibold ${
                              s.confidence >= 0.8 ? "bg-emerald-50 text-emerald-700"
                              : s.confidence >= 0.5 ? "bg-amber-50 text-amber-700"
                              : "bg-slate-100 text-slate-600"
                            }`}>
                              {Math.round(s.confidence * 100)}%
                            </span>
                          </TableCell>
                          <TableCell className="text-xs font-mono text-slate-700">{s.source_id}</TableCell>
                          <TableCell className="text-xs font-mono text-slate-700">{s.target_id}</TableCell>
                          <TableCell className="text-xs text-slate-600 max-w-[320px]">{s.rationale}</TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline"
                                    onClick={() => acceptSuggestion(s)}
                                    data-testid={`trace-sug-accept-${s.source_id}-${s.target_id}`}
                                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                              <Check className="w-3 h-3 mr-1" /> Accept
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {suggestions.note && (suggestions.suggestions || []).length > 0 && (
                <div className="text-xs text-slate-500 mt-2 italic">{suggestions.note}</div>
              )}
            </div>
          )}
        </div>

        {/* Trace table */}
        <div className="vq-card p-0 overflow-hidden" data-testid="trace-list-panel">
          <div className="p-5 flex items-center justify-between border-b border-slate-100">
            <div className="font-display font-bold text-slate-900">
              Trace links {loading ? "(loading…)" : `(${traces.length})`}
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table data-testid="trace-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead>Relation</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {traces.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-slate-500 py-8" data-testid="trace-empty">
                      No trace links yet. Add the first one above to start building the spine.
                    </TableCell>
                  </TableRow>
                )}
                {traces.map((t) => (
                  <TableRow key={t.trace_id} data-testid={`trace-row-${t.trace_id}`}>
                    <TableCell>
                      <div className="text-xs font-mono text-slate-500">{t.source_type}</div>
                      <div className="font-semibold text-slate-900 text-sm">{t.source_id}</div>
                    </TableCell>
                    <TableCell>
                      <span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-mono">
                        {t.relation}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs font-mono text-slate-500">{t.target_type}</div>
                      <div className="font-semibold text-slate-900 text-sm">{t.target_id}</div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 max-w-[240px] truncate">{t.notes || ""}</TableCell>
                    <TableCell className="text-[11px] text-slate-500 font-mono">
                      {t.created_at ? new Date(t.created_at).toLocaleDateString() : ""}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => removeTrace(t.trace_id)}
                              data-testid={`trace-delete-${t.trace_id}`}
                              className="text-rose-600 hover:text-rose-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
